import { useMemo, useCallback } from 'react';
import { User as AuthUser } from '../../../api/authApi';
import * as adminApi from '../../../api/adminApi';
import styles from './useAdminUtils.module.css';
import type { SectionKey } from '../components/layout';

interface UserCategoryData extends Record<string, string | number> {
  label: string;
  value: number;
  color: string;
}

export const useAdminUtils = (
  adminProfile: AuthUser | null,
  stats: adminApi.AdminDashboardStats | null,
  documents: adminApi.AdminDocument[],
  transcripts: adminApi.AdminYoutubeTranscript[],
  users: adminApi.AdminUserSummary[],
  admins: adminApi.AdminSummary[],
) => {
  // Format utilities
  const formatAdminText = useCallback((value?: string | null) => {
    if (typeof value !== 'string') return '—';
    return value.trim() ? value : '—';
  }, []);

  const formatAdminDate = useCallback((value?: string | null) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  }, []);

  // Profile Metadata
  const profileMeta = useMemo(() => {
    const displayName = adminProfile?.full_name?.trim() || 'Admin';
    const initial = displayName.charAt(0).toUpperCase();
    const email = adminProfile?.email ?? 'admin@policyora.ai';
    const joinLabel = adminProfile?.created_at
      ? new Date(adminProfile.created_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : 'just now';

    return {
      profileDisplayName: displayName,
      profileInitial: initial,
      profileEmail: email,
      profileJoinLabel: joinLabel,
      welcomeLabel: 'Welcome back',
    };
  }, [adminProfile]);

  // Dashboard Metrics
  const overviewMetrics = useMemo(() => {
    return [
      {
        label: 'Total users',
        value: stats?.users.users ?? 0,
        badge: `${styles.heroCard} ${styles.heroCardBlue}`,
        actionSection: 'users' as SectionKey,
      },
      {
        label: 'Admins',
        value: stats?.users.admins ?? 0,
        badge: `${styles.heroCard} ${styles.heroCardIndigo}`,
        actionSection: 'admins' as SectionKey,
      },
      {
        label: 'Documents',
        value: documents.length,
        badge: `${styles.heroCard} ${styles.heroCardGreen}`,
      },
      {
        label: 'Transcripts',
        value: transcripts.length,
        badge: `${styles.heroCard} ${styles.heroCardOrange}`,
      },
    ];
  }, [stats, documents.length, transcripts.length]);

  const resourceCards = useMemo(() => [
    {
      label: 'Documents',
      value: documents.length,
      description: 'Files uploaded by admin',
      accentClass: styles.resourceAccentGreen,
    },
    {
      label: 'Jurisdictions',
      value: stats?.jurisdictions?.total ?? 0,
      description: 'Regions covered',
      accentClass: styles.resourceAccentIndigo,
    },
    {
      label: 'Transcripts',
      value: transcripts.length,
      description: 'YouTube insights saved',
      accentClass: styles.resourceAccentPink,
    },
  ], [documents.length, stats?.jurisdictions?.total, transcripts.length]);

  const adminUserIds = useMemo(() => {
    const ids = new Set<number>();
    const adminList = admins ?? [];
    adminList.forEach(admin => {
      if (admin.user_id != null) {
        ids.add(admin.user_id);
      }
    });
    return ids;
  }, [admins]);

  const userCategoryData = useMemo<UserCategoryData[]>(() => {
    const counts = { research: 0, startup: 0, corporation: 0, government: 0, other: 0 };
    const userOnlyList = users.filter(user => !adminUserIds.has(user.id));

    userOnlyList.forEach(user => {
      const type = (user.organization_type || '').toLowerCase().trim();
      const tier = (user.plan_tier || '').toLowerCase();
      if (['academic institution', 'individual researcher'].includes(type)) counts.research++;
      else if (type === 'startup') counts.startup++;
      else if (type === 'corporation' || tier === 'enterprise') counts.corporation++;
      else if (type === 'government agency') counts.government++;
      else counts.other++;
    });
    return [
      { label: 'Researchers & Students', value: counts.research, color: '#444ce7' },
      { label: 'Startups', value: counts.startup, color: '#8098f9' },
      { label: 'Enterprises', value: counts.corporation, color: '#039855' },
      { label: 'Government', value: counts.government, color: '#f79009' },
      { label: 'Other(NPO,Firms)', value: counts.other, color: 'var(--ds-text-muted)' },
    ];
  }, [users, adminUserIds]);

  // Report Generation
  const handleGenerateReport = useCallback(() => {
    const timestamp = new Date().toLocaleString();
    const statsLines = [
      `Total users: ${stats?.users.users ?? 0}`,
      `Admins: ${stats?.users.admins ?? 0}`,
      `Documents: ${documents.length}`,
      `Transcripts: ${transcripts.length}`,
    ];
    const documentLines = documents.length
      ? documents.map(doc => `• ${doc.original_filename} (${new Date(doc.created_at).toLocaleDateString()})`)
      : ['• No documents uploaded'];
    const transcriptLines = transcripts.length
      ? transcripts.map(t => `• ${t.title || 'Untitled'} (${new Date(t.created_at).toLocaleDateString()})`)
      : ['• No transcripts generated'];
    const report = `Policyora Admin Report\nGenerated: ${timestamp}\n\nStats\n${statsLines.join('\n')}\n\nDocuments\n${documentLines.join('\n')}\n\nTranscripts\n${transcriptLines.join('\n')}`;
    const blob = new Blob([report], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `policyora-report-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, [documents, stats, transcripts]);

  return {
    formatAdminText,
    formatAdminDate,
    profileMeta,
    overviewMetrics,
    resourceCards,
    userCategoryData,
    handleGenerateReport,
  };
};
